import React, { useState, useEffect } from 'react';
import './AdminPage.css';
import { appConfig } from './config/appConfig';

function AdminPage() {
  const [bbid, setBbid] = useState('');
  const [propertyData, setPropertyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState({
    width: '100%',
    height: 'auto',
    theme: 'light',
    primaryColor: '#2997dc'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(400);
  const [codeCopied, setCodeCopied] = useState(false);

  // Listen for iframe resize messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'widget-resize' && widgetConfig.height === 'auto') {
        const height = Math.max(400, event.data.height + 40);
        console.log('Resizing preview iframe to:', height);
        setIframeHeight(height);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [widgetConfig.height]);

  const testBBID = async () => {
    if (!bbid.trim()) {
      setError('Please enter a BBID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPropertyData(null);

    try {
      console.log('Testing BBID on mobile/desktop...', { 
        userAgent: navigator.userAgent,
        isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      });
      
      // Test the BBID by making a sample API call
      const response = await fetch('https://checkavailability.alldigital.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
        },
        mode: 'cors',
        body: JSON.stringify({
          bbid: bbid.trim(),
          startdate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
          enddate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 8 days from now
          includepackages: true,
          strictsearch: false
        })
      });
      
      console.log('Response status:', response.status, response.statusText);

      const data = await response.json();
      console.log('BBID Test Response:', data);

      if (data.success && data.data?.roomtypes) {
        setPropertyData({
          bbid: bbid.trim(),
          propertyName: data.data.name || 'Unknown Property',
          roomCount: data.data.roomtypes.length,
          roomTypes: data.data.roomtypes
        });
        setError(null);
      } else {
        // More detailed error message based on API response
        let errorMessage = `BBID ${bbid} not found or has no rooms.`;
        
        if (data.error) {
          errorMessage += ` API Error: ${JSON.stringify(data.error)}`;
        } else if (!data.success) {
          errorMessage += ` API returned success: false`;
        } else if (!data.data) {
          errorMessage += ` No data in API response`;
        } else if (!data.data.roomtypes) {
          errorMessage += ` No room types found for this property`;
        }
        
        console.log('Detailed error info:', { response: data, bbid });
        setError(errorMessage);
        setPropertyData(null);
      }
    } catch (err) {
      console.error('Error testing BBID:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      
      let errorMessage = 'Failed to connect to availability service. ';
      
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        errorMessage += 'Network error - please check your internet connection.';
      } else if (err.name === 'AbortError') {
        errorMessage += 'Request timed out - please try again.';
      } else {
        errorMessage += `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setPropertyData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const generateEmbedCode = () => {
    if (!propertyData) return '';

    const config = {
      bbid: propertyData.bbid,
      width: widgetConfig.width,
      height: widgetConfig.height,
      theme: widgetConfig.theme,
      primaryColor: widgetConfig.primaryColor
    };

    // Use production domain when not on localhost
    const baseUrl = window.location.hostname === 'localhost' 
      ? appConfig.widget.developmentUrl 
      : appConfig.widget.productionUrl;

    // Remove trailing slash if present to avoid double slashes
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

    return `<!-- NightsBridge Availability Widget -->
<div id="availability-widget" data-config='${JSON.stringify(config)}'></div>
<script src="${cleanBaseUrl}/widget.js"></script>`;
  };

  const generateDirectLink = () => {
    if (!propertyData) return '';
    const params = new URLSearchParams({
      bbid: propertyData.bbid,
      theme: widgetConfig.theme,
      color: widgetConfig.primaryColor.replace('#', '')
    });
    
    // Always use current domain for preview to ensure we see the widget, not main app
    const baseUrl = window.location.origin;
    
    return `${baseUrl}/widget?${params.toString()}`;
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <header className="admin-header">
          <h1>Property Widget Generator</h1>
          <p>Generate a booking widget for your property website</p>
        </header>

        {/* BBID Configuration */}
        <section className="config-section">
          <h2>1. Configure Your Property</h2>
          <div className="bbid-input-group">
            <label htmlFor="bbid">Enter your NightsBridge Property ID (BBID):</label>
            <div className="input-with-button">
              <input
                id="bbid"
                type="text"
                value={bbid}
                onChange={(e) => setBbid(e.target.value)}
                placeholder="e.g., 29515"
                className="bbid-input"
                onKeyPress={(e) => e.key === 'Enter' && testBBID()}
              />
              <button 
                onClick={testBBID} 
                disabled={isLoading || !bbid.trim()}
                className="test-button"
              >
                {isLoading ? 'Fetching...' : 'Fetch Property'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          {propertyData && (
            <div className="property-info">
              <h3>✅ Property Found!</h3>
              <div className="property-details">
                <p><strong>Property:</strong> {propertyData.propertyName}</p>
                <p><strong>BBID:</strong> {propertyData.bbid}</p>
                <p><strong>Room Types:</strong> {propertyData.roomCount}</p>
              </div>
              <details className="room-list">
                <summary>View Room Types ({propertyData.roomCount})</summary>
                <ul>
                  {propertyData.roomTypes.map((room, index) => (
                    <li key={index}>
                      <strong>{room.rtname}</strong> (ID: {room.rtid})
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </section>

        {/* Widget Customization */}
        {propertyData && (
          <section className="config-section">
            <h2>2. Customize Your Widget</h2>
            <div className="customization-grid">
              <div className="form-group">
                <label htmlFor="width">Widget Width:</label>
                <select 
                  id="width"
                  value={widgetConfig.width}
                  onChange={(e) => setWidgetConfig({...widgetConfig, width: e.target.value})}
                >
                  <option value="100%">Full Width - Fills container</option>
                  <option value="800px">Large - 800px wide</option>
                  <option value="600px">Medium - 600px wide</option>
                  <option value="400px">Small - 400px wide</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="height">Widget Height:</label>
                <select 
                  id="height"
                  value={widgetConfig.height}
                  onChange={(e) => setWidgetConfig({...widgetConfig, height: e.target.value})}
                >
                  <option value="auto">Auto Height - Fits content</option>
                  <option value="600px">Standard - 600px tall</option>
                  <option value="800px">Tall - 800px tall</option>
                  <option value="500px">Compact - 500px tall</option>
                  <option value="400px">Short - 400px tall</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="theme">Theme:</label>
                <select 
                  id="theme"
                  value={widgetConfig.theme}
                  onChange={(e) => setWidgetConfig({...widgetConfig, theme: e.target.value})}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="primaryColor">Primary Color:</label>
                <div className="color-picker-wrapper">
                  <div className="color-picker-container">
                    <div 
                      className="round-color-picker"
                      style={{ backgroundColor: widgetConfig.primaryColor }}
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    >
                    </div>
                    {showColorPicker && (
                      <div className="hex-color-picker">
                        <div className="preset-colors">
                          {['#2997dc', '#e53e3e', '#38a169', '#805ad5', '#d69e2e', '#ed8936', '#000000', '#553c9a', '#319795', '#6b7280'].map(color => (
                            <div
                              key={color}
                              className="preset-color"
                              style={{ backgroundColor: color }}
                              onClick={() => {
                                setWidgetConfig({...widgetConfig, primaryColor: color});
                                setShowColorPicker(false);
                              }}
                            />
                          ))}
                        </div>
                        <div className="hex-input-section">
                          <input
                            type="text"
                            value={widgetConfig.primaryColor}
                            onChange={(e) => {
                              const hexValue = e.target.value;
                              if (/^#[0-9A-Fa-f]{0,6}$/.test(hexValue) || hexValue === '') {
                                setWidgetConfig({...widgetConfig, primaryColor: hexValue});
                              }
                            }}
                            placeholder="#2997dc"
                            className="picker-hex-input"
                            maxLength="7"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={widgetConfig.primaryColor}
                    onChange={(e) => {
                      const hexValue = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(hexValue) || hexValue === '') {
                        setWidgetConfig({...widgetConfig, primaryColor: hexValue});
                      }
                    }}
                    placeholder="#2997dc"
                    className="hex-input"
                    maxLength="7"
                  />
                </div>
              </div>
            </div>
            
            {/* Preview Button */}
            <div style={{ textAlign: 'center', margin: '2rem 0' }}>
              <button 
                className="test-button"
                onClick={() => document.getElementById('preview-section').scrollIntoView({ behavior: 'smooth' })}
                style={{ 
                  background: '#2997dc',
                  color: 'white',
                  border: 'none',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Preview Your Widget
              </button>
            </div>
          </section>
        )}

        {/* Generated Code */}
        {propertyData && (
          <section id="code-section" className="config-section">
            <h2>3. Get Your Widget Code</h2>
            
            <div className="code-section">
              <div className="code-header">
                <h3>Embed Code (Add to your website):</h3>
                <button 
                  className="modern-copy-button"
                  onClick={() => {
                    navigator.clipboard.writeText(generateEmbedCode());
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                >
                  {codeCopied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      Copy Code
                    </>
                  )}
                </button>
              </div>
              <div className="code-block">
                <pre><code>{generateEmbedCode()}</code></pre>
              </div>
            </div>



            <div className="instructions">
              <h3>📖 How to Use:</h3>
              <ol>
                <li><strong>Copy the embed code</strong> above</li>
                <li><strong>Paste it into your website</strong> where you want the booking widget to appear</li>
                <li><strong>Save and publish</strong> your website</li>
                <li><strong>Test the widget</strong> using the direct link to make sure it works</li>
              </ol>
              
              <div className="tips">
                <h4>💡 Tips:</h4>
                <ul>
                  <li>The widget is fully responsive and will adapt to your website's design</li>
                  <li>Customers can select dates and see real-time availability for your property</li>
                  <li>Booking links will direct customers to your NightsBridge booking page</li>
                  <li>The widget automatically blocks unavailable dates from your calendar</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Preview */}
        {propertyData && (
          <section id="preview-section" className="config-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: '0' }}>4. Preview Your Widget</h2>
              <button 
                className="modern-copy-button"
                onClick={() => document.getElementById('code-section').scrollIntoView({ behavior: 'smooth' })}
                style={{ 
                  background: '#2997dc',
                  color: 'white',
                  borderColor: '#2997dc'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Get Your Code
              </button>
            </div>
            <div className="preview-container">
              <iframe
                src={generateDirectLink()}
                width={widgetConfig.width}
                height={widgetConfig.height === 'auto' ? `${iframeHeight}px` : widgetConfig.height}
                style={{ 
                  border: 'none', 
                  borderRadius: '8px', 
                  display: 'block'
                }}
                title="Widget Preview"
                onLoad={(e) => {
                  if (widgetConfig.height === 'auto') {
                    // Try to get the content height and resize iframe
                    try {
                      const iframe = e.target;
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                      // Use multiple height calculations to get the maximum
                      const bodyHeight = iframeDoc.body.scrollHeight;
                      const documentHeight = iframeDoc.documentElement.scrollHeight;
                      const contentHeight = Math.max(bodyHeight, documentHeight);
                      const height = Math.max(400, contentHeight + 40);
                      console.log('Direct iframe height calculation:', height);
                      setIframeHeight(height);
                      
                      // Also set up a delayed check for dynamic content
                      setTimeout(() => {
                        const newBodyHeight = iframeDoc.body.scrollHeight;
                        const newDocumentHeight = iframeDoc.documentElement.scrollHeight;
                        const newContentHeight = Math.max(newBodyHeight, newDocumentHeight);
                        const newHeight = Math.max(400, newContentHeight + 40);
                        if (newHeight > height) {
                          console.log('Delayed iframe height update:', newHeight);
                          setIframeHeight(newHeight);
                        }
                      }, 1000);
                      
                    } catch (error) {
                      // Cross-origin issues, fallback to message listener
                      console.log('Using fallback height calculation, waiting for messages');
                      // Set a larger default for cross-origin scenarios
                      setIframeHeight(800);
                    }
                  }
                }}
              />
            </div>
          </section>
        )}

        <footer className="admin-footer">
          <p>Need help? Contact our support team or check the documentation.</p>
          <a href="/" className="back-link">← Back to Demo</a>
        </footer>
      </div>
    </div>
  );
}

export default AdminPage;
